/**
 * RiesgoRepo sobre Supabase. Misma lógica que DexieRiesgoRepo.
 */

import type { RiesgoRepo } from "../../repos/RiesgoRepo";
import type { Riesgo, RiesgoHistorialEntry, EstadoRiesgo } from "@app-types/entities";
import { UMBRALES_NIVEL_DEFAULT, nivelDesdeScore } from "@features/riesgos/catalogoDefault";
import { SupabaseBaseRepo } from "./SupabaseBaseRepo";

export class SupabaseRiesgoRepo extends SupabaseBaseRepo<Riesgo> implements RiesgoRepo {
  constructor(tenantId: string) {
    super("riesgo", tenantId);
  }

  async list(filter?: { afeId?: string; estado?: EstadoRiesgo }): Promise<Riesgo[]> {
    let rows = await this.listAll();
    if (filter?.afeId) rows = rows.filter((r) => r.pinneadoAfeIds.includes(filter.afeId!));
    if (filter?.estado) rows = rows.filter((r) => r.estado === filter.estado);
    return rows.sort((a, b) => a.codigo.localeCompare(b.codigo, undefined, { numeric: true }));
  }

  async get(id: string): Promise<Riesgo | null> {
    return this.getById(id);
  }

  async listByAfe(afeId: string): Promise<Riesgo[]> {
    return this.list({ afeId });
  }

  async create(
    data: Omit<
      Riesgo,
      "id" | "tenantId" | "createdAt" | "updatedAt" | "version" | "codigo" | "score" | "nivel" | "createdBy" | "updatedBy"
    >,
    userEmail: string,
  ): Promise<string> {
    const id = crypto.randomUUID();
    const now = new Date().toISOString();
    const codigo = await this.siguienteCodigo();
    const score = data.probabilidad * data.impacto;
    const nivel = data.tipo === "OPORTUNIDAD" ? "OPORTUNIDAD" : nivelDesdeScore(score, UMBRALES_NIVEL_DEFAULT);
    const nuevo: Riesgo = {
      ...data,
      id,
      tenantId: this.tenantId,
      codigo,
      score,
      nivel,
      version: 1,
      createdAt: now,
      updatedAt: now,
      createdBy: userEmail,
      updatedBy: userEmail,
      historial: [
        { fecha: now, usuario: userEmail, cambio: "CREADO", pNuevo: data.probabilidad, iNuevo: data.impacto, estadoNuevo: data.estado },
      ],
    };
    await this.createRaw(nuevo);
    return id;
  }

  async update(id: string, data: Partial<Riesgo>, expectedVersion: number): Promise<void> {
    const current = await this.get(id);
    if (!current) throw new Error(`Riesgo ${id} no encontrado`);
    const now = new Date().toISOString();
    const probabilidad = data.probabilidad ?? current.probabilidad;
    const impacto = data.impacto ?? current.impacto;
    const score = probabilidad * impacto;
    const tipo = data.tipo ?? current.tipo;
    const nivel = tipo === "OPORTUNIDAD" ? "OPORTUNIDAD" : nivelDesdeScore(score);
    const historial = [...(current.historial ?? [])];
    if (
      (data.probabilidad !== undefined && data.probabilidad !== current.probabilidad) ||
      (data.impacto !== undefined && data.impacto !== current.impacto)
    ) {
      historial.push({
        fecha: now,
        usuario: current.updatedBy,
        cambio: "P_I_CAMBIO",
        pAnterior: current.probabilidad,
        pNuevo: probabilidad,
        iAnterior: current.impacto,
        iNuevo: impacto,
      });
    }
    const updated: Riesgo = {
      ...current,
      ...data,
      probabilidad,
      impacto,
      score,
      nivel,
      historial,
      version: current.version + 1,
      updatedAt: now,
    };
    await this.updateRaw(updated, expectedVersion);
  }

  async changeState(id: string, nextState: EstadoRiesgo, userEmail: string, observaciones?: string): Promise<void> {
    const current = await this.get(id);
    if (!current) throw new Error(`Riesgo ${id} no encontrado`);
    if (current.estado === nextState) return;
    const now = new Date().toISOString();
    const entry: RiesgoHistorialEntry = {
      fecha: now,
      usuario: userEmail,
      cambio: "ESTADO_CAMBIO",
      estadoAnterior: current.estado,
      estadoNuevo: nextState,
      ...(observaciones !== undefined && { observaciones }),
    };
    await this.putRaw({
      ...current,
      estado: nextState,
      historial: [...(current.historial ?? []), entry],
      version: current.version + 1,
      updatedAt: now,
      updatedBy: userEmail,
    });
  }

  async pinAfe(id: string, afeId: string, userEmail: string): Promise<void> {
    const current = await this.get(id);
    if (!current) throw new Error(`Riesgo ${id} no encontrado`);
    if (current.pinneadoAfeIds.includes(afeId)) return;
    const now = new Date().toISOString();
    await this.putRaw({
      ...current,
      pinneadoAfeIds: [...current.pinneadoAfeIds, afeId],
      historial: [...(current.historial ?? []), { fecha: now, usuario: userEmail, cambio: "PINNEADO_AFE", observaciones: `AFE ${afeId}` }],
      version: current.version + 1,
      updatedAt: now,
      updatedBy: userEmail,
    });
  }

  async unpinAfe(id: string, afeId: string, userEmail: string): Promise<void> {
    const current = await this.get(id);
    if (!current) throw new Error(`Riesgo ${id} no encontrado`);
    if (!current.pinneadoAfeIds.includes(afeId)) return;
    const now = new Date().toISOString();
    await this.putRaw({
      ...current,
      pinneadoAfeIds: current.pinneadoAfeIds.filter((a) => a !== afeId),
      historial: [...(current.historial ?? []), { fecha: now, usuario: userEmail, cambio: "DESPINEADO_AFE", observaciones: `AFE ${afeId}` }],
      version: current.version + 1,
      updatedAt: now,
      updatedBy: userEmail,
    });
  }

  async delete(id: string): Promise<void> {
    await this.deleteById(id);
  }

  private async siguienteCodigo(): Promise<string> {
    const todos = await this.list();
    const max = todos
      .map((r) => parseInt(r.codigo.replace(/^R-/, ""), 10))
      .filter((n) => !isNaN(n))
      .reduce((m, n) => Math.max(m, n), 0);
    return `R-${String(max + 1).padStart(3, "0")}`;
  }
}
